import { Component, computed, inject, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { OpportunityService } from '../../../../core/services/opportunity.service';
import { SavedOpportunitiesStore } from '../../../espace-jeune/services/saved-opportunities.store';
import {
  OpportunityCard,
  OpportunityCardData,
  OpportunityCardTone,
} from '../../../../shared/ui/cards/opportunity-card/opportunity-card';
import { toOpportunityCardData } from '../../../../shared/utils/opportunity-card.mapper';
import { pollSilently } from '../../../../shared/utils/silent-polling';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

type OpportunityFilterKey = OpportunityCardTone | 'tous';

/** Nombre d'opportunités publiées chargé en une fois pour permettre un filtrage instantané côté client. */
const PAGE_SIZE = 100;
const DISPLAY_PAGE_SIZE = 8;

@Component({
  selector: 'app-opportunities-list',
  imports: [NgOptimizedImage, OpportunityCard, ScrollReveal, TranslatePipe],
  templateUrl: './opportunities-list.html',
  styleUrl: './opportunities-list.css',
})
export class OpportunitiesList {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly savedStore = inject(SavedOpportunitiesStore);

  /** Clé i18n de chaque filtre : `opportunities.allTypes` pour "tous", sinon `opportunities.types.<key>`. */
  readonly filters: { key: OpportunityFilterKey; labelKey: string }[] = [
    { key: 'tous', labelKey: 'opportunities.allTypes' },
    { key: 'emploi', labelKey: 'opportunities.types.emploi' },
    { key: 'formation', labelKey: 'opportunities.types.formation' },
    { key: 'financement', labelKey: 'opportunities.types.financement' },
    { key: 'innovation', labelKey: 'opportunities.types.innovation' },
    { key: 'citoyennete', labelKey: 'opportunities.types.citoyennete' },
  ];

  readonly activeFilter = signal<OpportunityFilterKey>('tous');
  readonly savedIds = this.savedStore.savedIds;

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly pageSize = DISPLAY_PAGE_SIZE;
  readonly currentPage = signal(0);
  private readonly opportunities = signal<OpportunityCardData[]>([]);

  readonly filtered = computed(() => {
    const filter = this.activeFilter();
    return filter === 'tous'
      ? this.opportunities()
      : this.opportunities().filter((o) => o.tone === filter);
  });

  readonly count = computed(() => this.filtered().length);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.count() / this.pageSize)));
  readonly paginated = computed(() => {
    const start = this.currentPage() * this.pageSize;
    const end = start + this.pageSize;
    return this.filtered().slice(start, end);
  });

  constructor() {
    this.fetchOpportunities();
    // Rafraîchissement silencieux : les nouvelles opportunités publiées apparaissent sans que le
    // visiteur ait à recharger la page (pas de spinner, pas de reset de pagination/filtre).
    pollSilently(() => this.fetchOpportunities(true));
  }

  private fetchOpportunities(silent = false): void {
    if (!silent) {
      this.loading.set(true);
      this.error.set(false);
    }
    this.opportunityService.list({ size: PAGE_SIZE, sort: 'publishedAt,desc' }).subscribe({
      next: (page) => {
        this.opportunities.set(page.content.map(toOpportunityCardData));
        this.loading.set(false);
      },
      error: () => {
        if (!silent) {
          this.loading.set(false);
          this.error.set(true);
        }
      },
    });
  }

  setFilter(key: OpportunityFilterKey): void {
    this.activeFilter.set(key);
    this.currentPage.set(0);
  }

  setPage(page: number): void {
    const totalPages = this.totalPages();
    const safePage = Math.max(0, Math.min(page, totalPages - 1));
    this.currentPage.set(safePage);
  }

  toggleSave(opportunityId: string): void {
    if (!this.auth.isAuthenticated()) {
      this.router.navigate(['/auth/login']);
      return;
    }
    this.savedStore.toggle(opportunityId);
  }

  openOpportunity(slug: string): void {
    this.router.navigate(['/opportunities', slug]);
  }
}
